import React, { useState } from "react";
import Heart from "../assets/heart11.png";
import Star from "../assets/star111.png";
import Globe from "../assets/global111.png";
import DollarSign from "../assets/dollar111.png";

const Button = ({ children, className = "", variant, ...props }) => {
    const base =
        "px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none";
    const styles =
        variant === "outline"
            ? "border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
            : "text-white bg-purple-600 hover:bg-purple-700";
    return (
        <button className={`${base} ${styles} ${className}`} {...props}>
            {children}
        </button>
    );
};

const Textarea = ({ className = "", ...props }) => (
    <textarea
        className={`w-full border border-gray-300 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 ${className}`}
        {...props}
    />
);

const Progress = ({ value, className = "" }) => (
    <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${className}`}>
        <div
            className="bg-purple-600 h-full transition-all"
            style={{ width: `${value}%` }}
        />
    </div>
);

const Card = ({ children, className = "" }) => (
    <div className={`bg-white shadow-sm rounded-md border ${className}`}>
        {children}
    </div>
);

const CardHeader = ({ children }) => <div className="p-4 border-b">{children}</div>;
const CardTitle = ({ children, className = "" }) => (
    <h2 className={`text-lg font-semibold ${className}`}>{children}</h2>
);
const CardContent = ({ children, className = "" }) => (
    <div className={`p-4 ${className}`}>{children}</div>
);

const questions = [
    {
        id: "love-1",
        title: "What activities make you lose track of time?",
        category: "love",
        placeholder: "Describe the activities that completely absorb your attention...",
    },
    {
        id: "love-2",
        title: "What subjects or topics do you find yourself constantly learning about?",
        category: "love",
        placeholder: "Think about what you naturally gravitate toward...",
    },
    {
        id: "love-3",
        title: "What kind of conversations energize you the most?",
        category: "love",
        placeholder: "Reflect on the discussions that excite you...",
    },
    {
        id: "love-4",
        title: "What environments or settings make you feel most alive?",
        category: "love",
        placeholder: "Describe the places where you feel energized...",
    },
    {
        id: "love-5",
        title: "What dreams or aspirations have you held since childhood?",
        category: "love",
        placeholder: "Think about the persistent dreams you’ve had...",
    },
    {
        id: "goodAt-1",
        title: "What do people consistently ask for your help with?",
        category: "goodAt",
        placeholder: "Think of things people rely on you for...",
    },
    {
        id: "goodAt-2",
        title: "What comes naturally to you that others find difficult?",
        category: "goodAt",
        placeholder: "Skills that feel effortless to you...",
    },
    {
        id: "goodAt-3",
        title: "What achievements are you most proud of?",
        category: "goodAt",
        placeholder: "Highlight key achievements that show your strength...",
    },
    {
        id: "goodAt-4",
        title: "What skills have you developed that set you apart?",
        category: "goodAt",
        placeholder: "Technical or soft skills that are unique to you...",
    },
    {
        id: "goodAt-5",
        title: "What feedback do you consistently receive about your strengths?",
        category: "goodAt",
        placeholder: "Feedback from peers, leaders, friends...",
    },
    {
        id: "worldNeeds-1",
        title: "What problems in the world deeply concern you?",
        category: "worldNeeds",
        placeholder: "List issues that motivate you to take action...",
    },
    {
        id: "worldNeeds-2",
        title: "What positive change do you want to see in your community?",
        category: "worldNeeds",
        placeholder: "Changes you wish to create nearby...",
    },
    {
        id: "worldNeeds-3",
        title: "What causes or movements inspire you to get involved?",
        category: "worldNeeds",
        placeholder: "Think about social or global movements...",
    },
    {
        id: "worldNeeds-4",
        title: "What legacy do you want to leave behind?",
        category: "worldNeeds",
        placeholder: "What would you like to be remembered for?",
    },
    {
        id: "worldNeeds-5",
        title: "What would you want to teach or share with future generations?",
        category: "worldNeeds",
        placeholder: "Knowledge or values you’d pass on...",
    },
    {
        id: "paidFor-1",
        title: "What services or skills could you offer that people would pay for?",
        category: "paidFor",
        placeholder: "Skills that have financial value...",
    },
    {
        id: "paidFor-2",
        title: "What problems can you solve that people struggle with?",
        category: "paidFor",
        placeholder: "Tasks you're good at that others find hard...",
    },
    {
        id: "paidFor-3",
        title: "What would you do professionally if money wasn't a concern?",
        category: "paidFor",
        placeholder: "Dream job if money didn’t matter...",
    },
    {
        id: "paidFor-4",
        title: "What industries or fields align with your interests and skills?",
        category: "paidFor",
        placeholder: "Industries where you'd thrive...",
    },
    {
        id: "paidFor-5",
        title: "What unique value can you provide that others cannot?",
        category: "paidFor",
        placeholder: "Your one-of-a-kind combination of skills...",
    },
];

const categoryIcons = {
    love: Heart,
    goodAt: Star,
    worldNeeds: Globe,
    paidFor: DollarSign,
};

const categoryColors = {
    love: "text-red-600 bg-red-100",
    goodAt: "text-yellow-600 bg-yellow-100",
    worldNeeds: "text-green-600 bg-green-100",
    paidFor: "text-blue-600 bg-blue-100",
};

export default function IkigaiFillInBlank({ onComplete }) {
    const [answers, setAnswers] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);

    const currentQuestion = questions[currentIndex];
    const progress = ((currentIndex + 1) / questions.length) * 100;
    const hasAnswer =
        currentQuestion && answers[currentQuestion.id]?.trim().length > 0;

    const handleAnswerChange = (value) => {
        setAnswers((prev) => ({
            ...prev,
            [currentQuestion.id]: value,
        }));
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex((prev) => prev + 1);
        } else {
            if (typeof onComplete === "function") {
                onComplete(answers, questions); // ✅ Trigger completion
            }
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    if (!currentQuestion) {
        return <div className="text-center text-red-600">No questions found.</div>;
    }

    const IconComponent = categoryIcons[currentQuestion.category];
    const colorClass = categoryColors[currentQuestion.category];

    return (
        <div className="space-y-6 p-4 mx-auto" style={{ width: '85%' }}>

            <Card className="bg-gray-50">
                <CardContent className="p-4">
                    <h3 className="font-semibold mb-3">Your Progress by Category</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(categoryIcons).map(([category, Icon]) => {
                            const categoryQuestions = questions.filter((q) => q.category === category);
                            const answeredCount = categoryQuestions.filter(
                                (q) => answers[q.id]?.trim().length > 0
                            ).length;
                            const colorClass = categoryColors[category];

                            return (
                                <div key={category} className="text-center">
                                    <div className={`p-2 rounded-full ${colorClass} w-fit mx-auto mb-1`}>
                                        <img src={Icon} className="w-4 h-4" alt={`${category} icon`} />
                                    </div>
                                    <div className="text-xs font-medium capitalize">
                                        {category === "goodAt"
                                            ? "Good At"
                                            : category === "worldNeeds"
                                                ? "World Needs"
                                                : category === "paidFor"
                                                    ? "Paid For"
                                                    : "Love"}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {answeredCount}/{categoryQuestions.length}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
            
            <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                    <span>
                        Question {currentIndex + 1} of {questions.length}
                    </span>
                    <span>{Math.round(progress)}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
            </div>

            {/* Question Card */}
            <Card className="border-2">
                <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                        {/* <div className={`p-2 rounded-full ${colorClass}`}>
                            <img src={IconComponent} alt="icon" className="w-5 h-5" />
                        </div> */}
                        <div>
                            <CardTitle className="text-xl">{currentQuestion.title}</CardTitle>
                            <p className="text-sm text-gray-500 capitalize">
                                {currentQuestion.category === "goodAt"
                                    ? "What You're Good At"
                                    : currentQuestion.category === "worldNeeds"
                                        ? "What World Needs"
                                        : currentQuestion.category === "paidFor"
                                            ? "What You Can Be Paid For"
                                            : "What You Love"}
                            </p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Textarea
                        value={answers[currentQuestion.id] || ""}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        placeholder={currentQuestion.placeholder}
                        className="min-h-[120px] text-base"
                        maxLength={500}
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                        <span>Take your time to reflect deeply</span>
                        <span>{(answers[currentQuestion.id] || "").length}/500</span>
                    </div>
                </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-between">
                <Button onClick={handlePrevious} disabled={currentIndex === 0} variant="outline">
                    Previous
                </Button>
                <Button onClick={handleNext} disabled={!hasAnswer}>
                    {currentIndex === questions.length - 1 ? "Complete Assessment" : "Next Question"}
                </Button>
            </div>

            {/* Category Progress Overview */}

        </div>
    );
}
